import type { FormEvent, MouseEvent } from "react";
import { useState } from "react";
import { api } from "../api";

type Props = {
  token: string;
  onClose: () => void;
  onCreated?: () => void;
};

export function NewUserModal({ token, onClose, onCreated }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !busy) onClose();
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    const u = username.trim();
    if (u.length < 2) {
      setError("Username must be at least 2 characters.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.createUser(token, { username: u, password, is_admin: isAdmin });
      onCreated?.();
      onClose();
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full flex flex-col"
        role="dialog"
        aria-labelledby="new-user-modal-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 id="new-user-modal-title" className="text-base font-semibold text-slate-900">
            New user
          </h3>
          <button type="button" onClick={onClose} className="btn shrink-0" disabled={busy} aria-label="Close">
            Close
          </button>
        </div>

        <form className="p-4 space-y-3" onSubmit={(ev) => void onSubmit(ev)}>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Username</label>
            <input
              type="text"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
              minLength={2}
              maxLength={128}
              placeholder="e.g. analyst.name@kepleranalytics.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Initial password (min 8 chars)</label>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            <p className="text-[11px] text-slate-500 mt-1">
              The user can change this from the portal once admins enable a self-service flow; for now use{" "}
              <strong>set password</strong> on their row to rotate.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm select-none">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <span>Make this user an admin</span>
          </label>
          <p className="text-[11px] text-slate-500 -mt-1">
            Admins can manage users, registrations, and grants. They still need <strong>schema grants</strong> assigned
            to read data via Claude.
          </p>

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
